import React, { ReactNode, useEffect, useRef } from 'react';
import {
  Button,
  Divider,
  Flex,
  ListItem,
  Text,
  UnorderedList,
  useDisclosure,
} from '@chakra-ui/react';
import ModalHeader from '@components/presentational/CustomModal/ModalHeader';
import ModalBody from '@components/presentational/CustomModal/ModalBody';
import Modal from '@components/presentational/CustomModal/Modal';
import { useSelector } from 'react-redux';
import { ReduxRootState } from '@/types/store/ReduxRootState';
import NextLink from 'next/link';
import useConnectQuery from '@components/app/hooks/useConnectQuery';
import { useRouter } from 'next/router';

interface Props {
  children: (onClick: () => void) => ReactNode;
  actionId: string;
  actionAfterLogin: ({ justLoggedIn }?: { justLoggedIn: boolean }) => void;
  accentColor?: 'menthe' | 'tournesol' | 'tuile' | 'brand';
}

/**
 * Checks if the user is authenticated. If the user is authenticated, it calls the onClick prop.
 * If the user is not authenticated, it opens a modal.
 *
 * /!\ CheckAuth components in the same page MUST have different actionId values
 * otherwise each component with the same actionId will trigger its action after login.
 */
export function CheckAuth({
  children,
  actionId,
  actionAfterLogin,
  accentColor,
}: Props) {
  const router = useRouter();
  const connectQuery = useConnectQuery({ executeActionAfterLogin: actionId });

  const { isOpen, onOpen, onClose } = useDisclosure();
  const user = useSelector(
    (state: ReduxRootState) => state.authentication.user
  );

  const handleClick = () => {
    if (user) {
      actionAfterLogin({ justLoggedIn: false });
    } else {
      onOpen();
    }
  };

  useEffect(() => {
    if (user && router.query?.executeActionAfterLogin === actionId) {
      actionAfterLogin({ justLoggedIn: true });
    }
  }, [router.query?.executeActionAfterLogin, user]);

  return (
    <>
      {children(handleClick)}
      <Modal
        accentColor={accentColor ? accentColor : 'brand'}
        isOpen={isOpen}
        onClose={onClose}
      >
        <ModalHeader>Authentification requise</ModalHeader>
        <ModalBody>
          <Flex direction="column" gap="1.5rem" width={['100%', '', '27.5rem']}>
            <Text>
              Ce contenu est uniquement accessible aux membres IFprofs.
              <br />
              Créez vous un compte gratuitement pour pouvoir bénéficier de toute
              la richesse du site :
            </Text>
            <UnorderedList>
              <ListItem>Ressources de qualité</ListItem>
              <ListItem>Formations en ligne</ListItem>
              <ListItem>Communauté active</ListItem>
            </UnorderedList>
            <Flex direction={'column'} alignItems={'center'} gap="1rem">
              <NextLink as={`/s-inscrire`} passHref href={`/s-inscrire`}>
                <Button as={'a'} size={'sm'}>
                  S'inscrire
                </Button>
              </NextLink>
              <Divider my={1} />
              <Text textAlign="center" color="black">
                J'ai déjà un compte
              </Text>
              <NextLink
                passHref
                href={{
                  pathname: '/se-connecter',
                  query: connectQuery,
                }}
                as={`/se-connecter`}
              >
                <Button as={'a'} size={'sm'}>
                  Me connecter
                </Button>
              </NextLink>
            </Flex>
          </Flex>
        </ModalBody>
      </Modal>
    </>
  );
}
